"use client";
import React from "react";

import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Link from "next/link";

import { GenericBannerProps } from "~/bff/components/GenericBanner";
import { CombinedLink } from "~/components/combined-link/component";
import { CTALinkButton } from "~/components/cta-link-button/component";
import { Image } from "~/components/image/component";
import { prepareTextAttribute } from "~/helpers/prepare-text-attribute";
import { getTestAutomationProps } from "~/helpers/test-automation-props";
import { EvergageContentZones } from "~/lib/salesforce/evergage/types";
import { TextColor } from "~/types/general.types";
import { getImageSrc } from "~/utils/image/get-image-src";

import { GENERIC_TEXT_IMAGE_BANNER_CLASSES, imageTemplates } from "./constants";
import { GenericTextImageBannerContainer } from "./styled";

export const GenericTextImageBanner: React.FC<GenericBannerProps> = ({
  image,
  link,
  textColor,
  title,
  text,
  testAutomationId = "generic-banner",
}) => {
  return (
    <GenericTextImageBannerContainer
      orientation={false}
      textColor={textColor ?? ""}
      id={testAutomationId ?? ""}
      {...getTestAutomationProps(testAutomationId)}
      data-evergage-zone={EvergageContentZones.GENERIC_BANNER}
    >
      <Container>
        <Grid
          className={GENERIC_TEXT_IMAGE_BANNER_CLASSES.grid}
          container
          alignItems="center"
        >
          <Grid item xs={12} sm={6} md={4}>
            <Typography
              className={GENERIC_TEXT_IMAGE_BANNER_CLASSES.title}
              variant="h2"
              {...getTestAutomationProps("title")}
            >
              {title}
            </Typography>
            <Typography
              className={GENERIC_TEXT_IMAGE_BANNER_CLASSES.text}
              component="p"
              variant="h6"
              {...getTestAutomationProps("text")}
            >
              {text}
            </Typography>
            <Link
              href={link?.url ?? ""}
              passHref
              legacyBehavior //NOSONAR `legacyBehavior` is recommended via official doc, if NextLink is depend on the nested link/button component
            >
              <CTALinkButton
                textColor={textColor as TextColor}
                href={link?.url ?? undefined}
              >
                {link?.label ?? ""}
              </CTALinkButton>
            </Link>
          </Grid>
          <Grid item xs={12} sm={6}>
            <CombinedLink href={link?.url ?? ""}>
              <Image
                className={GENERIC_TEXT_IMAGE_BANNER_CLASSES.image}
                imageTemplates={imageTemplates}
                src={getImageSrc(image?.source ?? undefined)}
                alt={prepareTextAttribute(image?.alt, true)}
                title={prepareTextAttribute(image?.title)}
                width="100%"
                fetchPriority="high"
              />
            </CombinedLink>
          </Grid>
        </Grid>
      </Container>
    </GenericTextImageBannerContainer>
  );
};
